import { css } from "@emotion/react";
import type { ReactNode } from "react";

import { createStaticStyles, cssFns } from "../../css";
import { useScreenDetector } from "../../screen-type";
import { colors } from "../../theme";
import { BaseButton, type BaseButtonProps } from "../base-button";
import { ClearIcon, DropdownIcon } from "../icons";
import {
  createButtonStylesWaterfall,
  useButtonStylesWaterfall,
} from "../styles";

const buttonStyles = createButtonStylesWaterfall({
  idle: {
    common: [
      cssFns.typo({ level: "body-1", weight: "regular" }),
      cssFns.border({
        radius: "36px",
        width: 0,
        color: "transparent",
      }),
      {
        color: colors.principal.white,
        backgroundColor: colors.principal.ebblue,
        height: 32,
      },
    ],
    mobile: cssFns.padding("6px", "12px"),
    desktop: cssFns.padding("6px", "16px"),
  },
  pressed: {
    backgroundColor: colors.secondary.darkblue,
  },
});

type FilterSearchButtonProps = {
  text: string;
  icon?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
} & Pick<
  BaseButtonProps,
  "disabled" | "onPress" | "triggerRef" | "triggerProps" | "active" | "styles"
>;

export const FilterSearchButton = ({
  text,
  icon,
  clearable,
  onClear,
  ...baseProps
}: FilterSearchButtonProps) => {
  const { waterfall } = useButtonStylesWaterfall(buttonStyles);
  const { isMobile } = useScreenDetector();

  const isClearVisible = clearable && baseProps.active;

  return (
    <BaseButton type="button" waterfall={waterfall} {...baseProps}>
      {text}
      {(isClearVisible || icon) && (
        <div
          css={{
            display: "flex",
            gap: 4,
            alignItems: "center",
          }}
        >
          {isClearVisible && (
            <ClearIcon
              variant={isMobile ? "transparent" : "circled"}
              onPress={onClear}
            />
          )}
          {icon}
        </div>
      )}
    </BaseButton>
  );
};

const dropdownStyles = {
  common: createStaticStyles.clean({
    justifyContent: "space-between",
    backgroundColor: colors.secondary.darkblue,
  }),
};

FilterSearchButton.Dropdown = (
  props: Omit<FilterSearchButtonProps, "icon"> & {
    dropdownState?: boolean;
  },
) => {
  return (
    <FilterSearchButton
      {...props}
      styles={css([props.styles, dropdownStyles.common])}
      icon={
        <DropdownIcon
          state={props.dropdownState}
          triggerProps={props.triggerProps}
        />
      }
    />
  );
};
